import {
  Alert,
  Button,
  CloseButton,
  Heading,
  Link,
  Modal,
  Spinner,
  Text,
} from "@instructure/ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  ERROR,
  SUCCESS,
  useNotification,
  WARNING,
} from "../../../../../components/CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  useStatus,
  WORKING,
} from "../../../../../components/CustomHooks/StatusProvider";
import { DialogYesNo } from "../../../../../components/DialogYesNo/DialogYesNo";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { TaskService } from "../../../../../services/TaskService";
import { TermsContext } from "../../../../../utils/Context";
import { sleep, waitForExpectedResult } from "../../../../../utils/Timer";
import {
  dueDateAfterTerm,
  getDueDate,
} from "../../Components/SectionCard/utils";
import SectionsByTermCard from "./SectionsByTermCard";

const MAX_RETRIES = 24;
const RETRY_DELAY_MS = 5000;

const BulkFinalizeResultsModal = ({
  open,
  setOpen,
  coordinatedSections,
  setCoordinatedSections,
  loadingSections,
  loadingError,
  defaultDeadline,
  courseCode,
}) => {
  const { t, i18n } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const showNotification = useNotification();
  const { updateStatus, toggleStatus } = useStatus();
  // Filter out terms that don't have a valid due date
  const filteredCourseList = Object.entries(coordinatedSections).filter(
    ([, sections]) =>
      sections.length > 0 &&
      getDueDate(
        sections[0].term,
        dueDateAfterTerm(sections[0], defaultDeadline),
        insightsTerms
      )
  );
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  function isPastDueDate(section, defaultDeadline, insightsTerms) {
    const dueDate = dueDateAfterTerm(section, defaultDeadline);
    const finalizationDeadline = getDueDate(
      section.term,
      dueDate,
      insightsTerms,
      i18n.language
    );
    return (
      finalizationDeadline instanceof Date && finalizationDeadline < new Date()
    );
  }

  async function handleSubmit() {
    try {
      const sectionsToFinalize = Object.values(coordinatedSections)
        .flat()
        .filter((s) => isPastDueDate(s, defaultDeadline, insightsTerms));
      const courseCanvasIds = sectionsToFinalize.map((s) => s.canvas_id);
      if (courseCanvasIds.length === 0) {
        showNotification(t("analyze.noSectionsToFinalize"), WARNING);
        return;
      }
      setFinalizing(true);

      const taskPayload = {
        title: "bulk-finalize-results-task",
        obj: { canvasCourseIds: courseCanvasIds },
      };

      const createdTask = await TaskService.createTask(taskPayload);

      const sharedState = {
        taskCompleted: false,
        verificationFailures: 0,
        total: courseCanvasIds.length,
      };

      const taskPromise = pollTaskUntilDone(
        createdTask.ddl_id,
        sharedState,
        courseCanvasIds
      );

      const verificationPromise = Promise.all(
        courseCanvasIds.map((canvas_id) =>
          processSection(canvas_id, createdTask.ddl_id, sharedState)
        )
      );

      await Promise.race([taskPromise, verificationPromise]);
    } catch {
      showNotification(t("analyze.bulkFinalizeError"), ERROR);
    } finally {
      setFinalizing(false);
    }
  }

  const processSection = async (canvas_id, taskId, sharedState) => {
    try {
      const results = await AssignmentService.getResultsByContext(
        AssignmentService.ROLLUP,
        canvas_id
      );

      await AssignmentService.finalizeResultsByContext({
        mode: AssignmentService.ROLLUP,
        roundingMode: "roundDown",
        contextId: canvas_id,
        data: JSON.stringify(results.data),
        bulkTaskId: taskId,
      });

      const verified = await handleVerification(canvas_id, taskId, sharedState);

      if (!verified) {
        sharedState.verificationFailures++;

        // If ALL sections failed verification
        if (
          sharedState.verificationFailures === sharedState.total &&
          !sharedState.taskCompleted
        ) {
          console.debug(
            "All verifications exhausted retries before task completed."
          );
        }
      }
    } catch (err) {
      console.debug("Section failed:", canvas_id, err);
    }
  };

  const handleVerification = async (sectionCanvasId, taskId, sharedState) => {
    let attempts = 0;

    while (attempts < MAX_RETRIES) {
      if (sharedState.taskCompleted) {
        return true; // short-circuit
      }

      attempts++;

      const isFinalized = await AssignmentService.getFinalizedResultsExist(
        sectionCanvasId,
        taskId
      );

      if (isFinalized) {
        return true;
      }

      await sleep(RETRY_DELAY_MS);
    }

    return false;
  };

  function markSectionsFinalized(canvasIds) {
    setCoordinatedSections((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([term, sections]) => {
        const remaining = sections.filter(
          (s) => !canvasIds.includes(s.canvas_id)
        );
        if (remaining.length > 0) {
          updated[term] = remaining;
        }
      });
      return updated;
    });
  }

  const pollTaskUntilDone = (taskId, sharedState, finalizedCanvasIds = []) => {
    showNotification(
      <Text>
        {t("notifications.results.bulkFinalize")}
        <Link
          themeOverride={{ fontWeight: "bold" }}
          onClick={() => toggleStatus(true)}
        >
          {t("status.checkStatus")}
        </Link>
      </Text>,
      SUCCESS,
      10000
    );

    updateStatus({
      service: {
        name: t("status.bulkFinalizeStatus"),
        status: WORKING,
        progress: 0,
      },
    });

    return new Promise((resolve) => {
      waitForExpectedResult(
        () => TaskService.getTaskById(taskId),

        (task) => {
          const total = task.obj?.canvasCourseIds?.length || 1;

          const progress = Math.min(
            100,
            parseInt((task.counter / total) * 100)
          );

          updateStatus({
            service: {
              name: t("status.bulkFinalizeStatus"),
              status: progress === 100 ? GOOD : WORKING,
              progress,
            },
          });

          const done = task.status === "done" || task.status === "error";

          if (done) {
            sharedState.taskCompleted = true;
          }

          return done;
        },

        (task) => {
          sharedState.taskCompleted = true;

          if (task.status === "done") {
            markSectionsFinalized(finalizedCanvasIds);
          }
          processBulkFinalizeTask(task);

          resolve(task);
        },

        () => {
          showNotification(
            t("notifications.results.bulkFinalizeFailed"),
            ERROR
          );

          updateStatus({
            service: { name: t("status.bulkFinalizeStatus"), status: FAIL },
          });

          resolve(null);
        },

        500,
        5000
      );
    });
  };

  function processBulkFinalizeTask(task) {
    if (task.status === "done") {
      showNotification(t("notifications.results.bulkFinalizeSuccess"), SUCCESS);
    } else if (task.status === "error") {
      showNotification(t("notifications.results.bulkFinalizeFailed"), ERROR);
      updateStatus({
        service: { name: t("status.bulkFinalizeStatus"), status: FAIL },
      });
    }
  }

  return (
    <Modal
      open={open}
      onDismiss={() => setOpen(false)}
      size="medium"
      label={t("user.coordinatedCourses")}
    >
      <Modal.Header>
        <Heading as="h3">{courseCode}</Heading>
        <CloseButton
          offset="medium"
          placement="end"
          onClick={() => setOpen(false)}
          screenReaderLabel={t("global.close")}
        />
      </Modal.Header>
      <Modal.Body>
        {loadingSections ? (
          <>
            <Skeleton
              style={{ marginBottom: "0.75rem", height: "100px" }}
              count={3}
            />
          </>
        ) : loadingError ? (
          <Alert variant="error">
            {t("notifications.general.fetchError", {
              obj: t("assessment.sections.sections"),
            })}
          </Alert>
        ) : filteredCourseList.length === 0 ? (
          <Alert>{t("finalizeCourse.noSectionsToFinalize")}</Alert>
        ) : (
          <>
            <div style={{ textAlign: "right" }}>
              <Button
                id="finalize-all-relevant-sections-button"
                size="small"
                onClick={() => setFinalizeConfirm(true)}
                margin="0 0 small 0"
                disabled={finalizing}
                color="primary"
              >
                {finalizing && (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                    margin="0 xx-small xxx-small 0"
                  />
                )}
                {t("analyze.autoFinalizeAllSectionResults")}
              </Button>
              <DialogYesNo
                message={t("finalizeCourse.bulkFinalizeConfirm")}
                openStatus={finalizeConfirm}
                onClickNo={() => setFinalizeConfirm(false)}
                onClickYes={() => {
                  setFinalizeConfirm(false);
                  handleSubmit();
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {filteredCourseList.map(([term, sections], key) => (
                <SectionsByTermCard
                  key={key}
                  sections={sections}
                  term={term}
                  courseCode={courseCode}
                  finalizingAll={finalizing}
                  defaultDeadline={defaultDeadline}
                  setCoordinatedSections={setCoordinatedSections}
                />
              ))}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
export default BulkFinalizeResultsModal;
